// src/pages/employeesPage/EmployeesPage.jsx
import React, { useEffect, useState } from "react";
import "./EmployeesPage.css";
import { Search, Plus, Edit, Trash2, X, Save } from "lucide-react";

export default function EmployeesPage() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    position: "",
    salary: "",
    hire_date: "",
    password: "",
  });
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");

  async function fetchEmployees() {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/employees`, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });
      const data = await res.json();
      if (Array.isArray(data)) setEmployees(data);
      else if (Array.isArray(data.employees)) setEmployees(data.employees);
      else setEmployees([]);
    } catch (err) {
      console.error("Error fetching employees:", err);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchEmployees();
  }, []);

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const url = editingEmployee
        ? `${process.env.REACT_APP_API_URL}/api/employees/${editingEmployee.id}`
        : `${process.env.REACT_APP_API_URL}/api/employees`;
      const method = editingEmployee ? "PUT" : "POST";
      const bodyData = { ...form };
      if (editingEmployee && !form.password) delete bodyData.password;

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        body: JSON.stringify(bodyData),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to save employee");

      if (editingEmployee) {
        setEmployees((prev) =>
          prev.map((emp) => (emp.id === editingEmployee.id ? data : emp))
        );
        setMessage("✅ Employee updated successfully!");
      } else {
        setEmployees((prev) => [...prev, data]);
        setMessage("✅ Employee added successfully!");
      }

      setShowModal(false);
      setEditingEmployee(null);
      setForm({
        name: "",
        email: "",
        position: "",
        salary: "",
        hire_date: "",
        password: "",
      });
    } catch (err) {
      setMessage(`❌ ${err.message}`);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("Are you sure you want to delete this employee?")) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/employees/${id}`, {
        method: "DELETE",
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to delete employee");
      setEmployees((prev) => prev.filter((emp) => emp.id !== id));
    } catch (err) {
      alert(`❌ ${err.message}`);
    }
  }

  function handleEdit(emp) {
    setEditingEmployee(emp);
    setForm({
      name: emp.name,
      email: emp.email,
      position: emp.position,
      salary: emp.salary,
      hire_date: emp.hire_date ? emp.hire_date.split("T")[0] : "",
      password: "",
    });
    setShowModal(true);
  }

  const filteredEmployees = employees.filter(
    (emp) =>
      emp.name.toLowerCase().includes(search.toLowerCase()) ||
      emp.email.toLowerCase().includes(search.toLowerCase()) ||
      emp.position.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="employees-container">
      {/* ===== Header Row ===== */}
      <div className="header-row">
        <h2>Nam-Mark Employees</h2>

        <div className="header-actions">
          <div className="search-box">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Search employees..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="search-input"
            />
          </div>
          <button className="add-btn" onClick={() => setShowModal(true)}>
            <Plus size={18} /> Add Employee
          </button>
        </div>
      </div>

      {loading ? (
        <p>Loading employees...</p>
      ) : (
        <table className="employees-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Position</th>
              <th>Salary</th>
              <th>Hire Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredEmployees.length > 0 ? (
              filteredEmployees.map((emp) => (
                <tr key={emp.id}>
                  <td>{emp.id}</td>
                  <td>{emp.name}</td>
                  <td>{emp.email}</td>
                  <td>{emp.position}</td>
                  <td>${emp.salary}</td>
                  <td>{new Date(emp.hire_date).toLocaleDateString()}</td>
                  <td>
                    <button className="edit-btn" onClick={() => handleEdit(emp)}>
                      <Edit size={16} /> Edit
                    </button>
                    <button
                      className="delete-btn"
                      onClick={() => handleDelete(emp.id)}
                    >
                      <Trash2 size={16} /> Delete
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7">No employees found</td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {/* ===== Add/Edit Modal ===== */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>{editingEmployee ? "Edit Employee" : "Add New Employee"}</h3>
            {message && (
              <div
                className={`message ${
                  message.startsWith("✅") ? "success" : "error"
                }`}
              >
                {message}
              </div>
            )}
            <form onSubmit={handleSubmit} className="employee-form">
              <input
                type="text"
                name="name"
                placeholder="Name"
                value={form.name}
                onChange={handleChange}
                required
              />
              <input
                type="email"
                name="email"
                placeholder="Email"
                value={form.email}
                onChange={handleChange}
                required
              />
              <input
                type="text"
                name="position"
                placeholder="Position"
                value={form.position}
                onChange={handleChange}
                required
              />
              <input
                type="number"
                name="salary"
                placeholder="Salary"
                value={form.salary}
                onChange={handleChange}
                required
              />
              <input
                type="date"
                name="hire_date"
                value={form.hire_date}
                onChange={handleChange}
                required
              />
              <input
                type="password"
                name="password"
                placeholder={
                  editingEmployee
                    ? "New Password (leave blank to keep old)"
                    : "Password"
                }
                value={form.password}
                onChange={handleChange}
                required={!editingEmployee}
              />

              <div className="modal-actions">
                <button type="submit">
                  <Save size={16} />{" "}
                  {editingEmployee ? "Update Employee" : "Add Employee"}
                </button>
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => {
                    setShowModal(false);
                    setEditingEmployee(null);
                    setForm({
                      name: "",
                      email: "",
                      position: "",
                      salary: "",
                      hire_date: "",
                      password: "",
                    });
                  }}
                >
                  <X size={16} /> Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
